import * as React from 'react';
import {identity, initial, last, isEqual, flow} from 'lodash';
import {emptyObject} from '@packages/utilities/fp/utils';
import {union} from '@packages/utilities/key-map';
import {
  WFID_ATTR,
  WF_INSTANCEID_ATTR,
  WF_DATA_ID_ATTR,
} from '@packages/utilities/html';
import {GRID_EDITING_FILTER} from './GridEditingStyles';
import {
  maybeAddEmptyClass,
  maybeAddGridClasses,
  shouldAddEmptyClass,
  shouldAddGridClass,
} from './EmptyNodeUtils';
import {
  PSEUDOS,
  PSEUDO_PREFIX,
} from '@packages/systems/core/constants/Selectors';
import {
  VariantStateKey,
  getUpstreamPseudos,
} from '@packages/systems/style-legacy/selectors';
import {singleMemo, weakMemo, cacheMemo} from '@packages/utilities/memo';
import {ContentEditor} from '@packages/systems/ContentEditorUi';
import {
  BINDING_ON_CHILDREN_ATTR,
  CMS_BINDING_ON_CHILDREN_ATTR,
  CMS_COLLECTION_ID_ATTR,
  CMS_COLLECTION_SOURCE_ATTR,
  CMS_ITEM_ID_ATTR,
  WEBFLOW_COMPONENT_ROOT_ATTR,
} from '@packages/systems/core/constants/SharedConfig';
import {
  getGuidNullable,
  __DEPRECATED__getNativeId,
  computeNativeId,
  NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import type {
  Component,
  NormalizedAtomConfig,
  ExpressionPath,
  EnvironmentContextType,
  ClassesList,
} from '@packages/systems/wfdl';
import {
  AtomNormalizedRender,
  ExpansionProps,
  evalElementMemo,
  useRuntimeEnvironment,
  usePreviewMode,
  useCssPropertyMap,
  useShowEmpty,
  useShowEmptyForBuildModeByKey,
  useAnalyzeMode,
  useOpenCanvas,
  useMediaQueryWhen,
} from '@packages/systems/render';
import {isNativeIdPath} from '@packages/shared/render/utils/ContentEditorUtils';
import {Expressions} from '@packages/systems/wfdl';
import {
  isEditingSelectedNodeInInstance as _isEditingSelectedNodeInInstance,
  elementContainsNestedSelection,
  getBoundExpressionAtPath,
  getCurrentComponent,
  getRelativePath,
} from '@packages/utilities/platform';
import {
  isComponentOnlyDropTarget,
  isSlot,
  isFrameLayoutElement,
  isBody,
  isDynamoItem,
  isTextHolder,
} from '@packages/utilities/elementTypeUtils';
import {isTypographicCandidate} from '@packages/utilities/platform-elements';
import {
  COMPONENT_SLOT_CLASS,
  getDropTargetClass,
} from '@packages/utilities/PageBuildUtils';
import {
  getDynamoBindingPaths,
  isDynamoChildrenBinding,
} from '@packages/systems/dynamo/wfdl';
import {transformDynamoPropsForDesigner} from '@packages/systems/dynamo/useDynamoProps';
import {BreakpointID, CssPropertyMap} from '@packages/systems/style/types';
import {PseudoStateKey} from '@packages/systems/core/utils/StyleTypes';
import type {DragDropState} from '@packages/systems/designer/dnd';
import {isCommerceCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {push} from '@packages/systems/core/utils/ImmUtils';
import {transformWithElementTrackingContextForDesigner} from '@packages/systems/element-tracking';
import {
  type SeenComponents,
  lastSeenComponentKind,
} from '@packages/systems/core/utils/SeenComponentsUtils';
import {
  isBoundElement,
  isDynBoundElement,
  hasDynamoUniversalBindings,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {useDesignerExpansionItems} from './ItemsContext';
import {mut_copyBindingProps} from '@packages/systems/dynamo/binding-props';

const selectedNativeIdPathKey = 'selectedNativeIdPath';

/**
 * Generates an Immutable adder that will conditionally add a class for elements where dropping is allowed
 * @param  {ExpressionElement}        element          The element to potentially add the class to.
 * @param  {Array<String>}            dropTargets      A List of abstract ids where dropping is allowed
 * @return {Function}                                  An Immutable adder to apply to an existing List of classes (value: List<T>) => List<T>
 */
const maybeAddDropAllowedClass = (
  element: ExpressionElement,
  dropTargets: DragDropState['dropTargets']
) => {
  /**
   * Immutable adder that conditionally adds a class for nodes where dropping is allowed to an existing list of classes.
   * @param  {List}  classes  The initial list of classes
   * @return {List}           The resulting list of classes
   */
  return (classes: ClassesList) => {
    return dropTargets.includes(element.id)
      ? classes.push('wf-drop-allowed')
      : classes;
  };
};

const createEmptyClassAdder = weakMemo((cssPropertyMap: CssPropertyMap) =>
  weakMemo(
    (element: ExpressionElement) =>
      (
        showEmpty: boolean,
        showEmptyAsBlock: boolean | undefined,
        mediaQuery: BreakpointID | null,
        pseudoMode: PseudoStateKey,
        variantState: VariantStateKey,
        data: ExpansionProps['data']
      ) => {
        return showEmpty && mediaQuery
          ? maybeAddEmptyClass(
              element,
              cssPropertyMap,
              mediaQuery,
              pseudoMode,
              variantState,
              showEmptyAsBlock,
              data
            )
          : identity;
      }
  )
);

const createDropTargetOrSlotClassAdder = singleMemo(
  weakMemo((environmentContext: EnvironmentContextType) =>
    weakMemo((components: Component.ComponentMap) =>
      weakMemo((element: ExpressionElement) => {
        const isPageBuildMode = environmentContext.get('buildMode');
        const isDropTarget = isComponentOnlyDropTarget(element);
        const isComponentSlot = isSlot(element);

        if (isPageBuildMode && isComponentSlot) {
          return push(COMPONENT_SLOT_CLASS);
        }

        const dropTargetClass = getDropTargetClass({
          components,
          isDropTarget,
          isPageBuildMode,
        });

        return dropTargetClass ? push(dropTargetClass) : identity;
      })
    )
  )
);

const createGridClassAdder = weakMemo((cssPropertyMap: CssPropertyMap) =>
  weakMemo(
    (element: ExpressionElement) =>
      (
        mediaQuery: BreakpointID | null,
        pseudoMode: PseudoStateKey,
        variantState: VariantStateKey
      ) =>
        maybeAddGridClasses(
          element,
          cssPropertyMap,
          mediaQuery,
          pseudoMode,
          variantState
        )
  )
);

const createDropAllowedClassAdder = weakMemo(
  (dropTargets: DragDropState['dropTargets']) =>
    weakMemo((element: ExpressionElement) => {
      return maybeAddDropAllowedClass(element, dropTargets);
    })
);

const createAffectedNodeClassAdder = weakMemo(
  (affectedNodeIds: Array<string>) =>
    weakMemo((element: ExpressionElement) => {
      return affectedNodeIds.includes(element.id)
        ? (classes: ClassesList) => classes.push('wf-affected')
        : identity;
    })
);

const numberOfPossiblePseudoModeValues = Object.keys(PSEUDOS).length + 1; // + 1 for `null`
const memoizePseudoMode = cacheMemo(numberOfPossiblePseudoModeValues);
const createPseudoClassAdder = memoizePseudoMode(
  (pseudoMode: PseudoStateKey) => (classes: ClassesList) => {
    return pseudoMode
      ? classes.push(
          ...[...getUpstreamPseudos(pseudoMode), pseudoMode].map(
            (pseudoSuffix) => PSEUDO_PREFIX + pseudoSuffix
          )
        )
      : classes;
  }
);

const showEmptyWithNode = (showEmpty: boolean, element: ExpressionElement) => {
  return showEmpty && !isBoundElement(element, 'innerHTML');
};

const focusedSymbolKey = 'focusedSymbolNativeIdPath';

const CHILDREN_BINDING_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];

const hasDynamoBindings = weakMemo(
  (expression: Expression): boolean =>
    getDynamoBindingPaths(expression).length > 0
);

/**
 * Returns whether a component root receives any data from a CMS binding.
 */
const isCMSBoundComponentRoot = (
  nativeIdPath: NativeId[],
  components: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[],
  isComponentRootElement: boolean
): boolean => {
  if (!isComponentRootElement) return false;

  const currentComponent = getCurrentComponent(
    focusedSymbolNativeIdPath,
    components
  );
  if (!currentComponent) return false;

  const componentInstanceData = getBoundExpressionAtPath(
    initial(getRelativePath(nativeIdPath, focusedSymbolNativeIdPath)),
    [],
    components,
    currentComponent
  );
  return (
    componentInstanceData !== null && hasDynamoBindings(componentInstanceData)
  );
};

/**
 * Determines if the current element is the root of a user component (symbol).
 * User components belong to the __SitePlugin namespace in the components map,
 * as opposed to plugin components which belong to other namespaces.
 */
const isUserComponentRootElement = (
  isComponentRootElement: boolean,
  seenComponents: SeenComponents
): boolean => {
  return (
    isComponentRootElement && lastSeenComponentKind(seenComponents) === 'Symbol'
  );
};

const generateUpdatedAttributes = (
  nativeId: NativeId | string,
  {
    attributes,
    environmentContext,
    pluginsContext,
    nativeIdPath,
    isComponentRootElement,
    element,
    components,
  }: ExpansionProps,
  expandedNativeIdPath: NativeId[],
  previewMode: boolean,
  shouldShowWebflowComponentRootAttr: boolean
) => {
  const canNavigateFromPageToCMS = environmentContext.get(
    'canNavigateFromPageToCMS'
  );
  const isNestedInTextHolderWithBoundHTML = environmentContext.get(
    'isNestedInTextHolderWithBoundHTML'
  );
  const customCodeExecutionAllowed = environmentContext.get(
    'customCodeExecutionAllowed'
  );

  // Add data-w-id attribute
  // First calculate which attribute key we need to use, as nodes inside of
  // unfocused component instances use data-w-instance-of, instead of data-w-id.
  // In case of focused nested symbols only children of the last symbol in the focused chain are given data-w-id attribute
  const key =
    nativeIdPath.length &&
    !isComponentRootElement &&
    last(environmentContext.get('focusedSymbolNativeIdPath')) !==
      last(nativeIdPath) &&
    // In Preview mode use WFID_ATTR otherwise the canvas click handler
    // logic breaks in preview mode.
    !previewMode
      ? WF_INSTANCEID_ATTR
      : WFID_ATTR;

  const childrenExpr = Expressions.getIn(
    Expressions.getElementData(element),
    CHILDREN_BINDING_PATH
  );

  // We preserve the `data-has-binding-on-children` attribute in preview mode when custom code is enabled,
  // in order to prevent unnecessary re-renders or iframe reloads when switching between preview and comment modes.
  // This is safe because the `.wf-custom-code-binding` class — which is triggered by this attribute — is only applied
  // when the workflow is in comment mode, so it has no impact in preview unless custom code is actively running.
  const hasBinding =
    (!previewMode || customCodeExecutionAllowed) &&
    childrenExpr &&
    (Expressions.isBinding(childrenExpr) ||
      Expressions.getCase(childrenExpr) !== null) &&
    !isSlot(element);

  const hasCmsChildrenBinding =
    hasBinding && isDynamoChildrenBinding(Expressions.getElementData(element));

  return attributes.withMutations((map: any) => {
    /**
     * When rendering a node within a Rich Text element that is bound to Rich Text CMS data, we do not want
     * to include any native id attributes: those ids would not lead to any elements that can be found in the
     * Designer's components map, however hover and selection on the Canvas heavily rely on these attributes being correct.
     */
    if (!isNestedInTextHolderWithBoundHTML) {
      map
        .set(key, nativeId)
        .set(WF_DATA_ID_ATTR, JSON.stringify(expandedNativeIdPath));
    }

    /**
     * Add component root attribute for selecting components inside the Content Editor.
     * Only applies to user components (symbols), not plugin components.
     */
    if (shouldShowWebflowComponentRootAttr) {
      map.set(WEBFLOW_COMPONENT_ROOT_ATTR, 'true');
    }

    // CMS-bound rich text gets a separate marker so its children cannot be
    // selected on-canvas before the Content Editor is active. The CSS lifts
    // that block after the editor sets `contenteditable` on this node.
    //
    // See also:
    // - site-designer.less (look for `data-has-binding-on-children` and
    //   `data-has-cms-binding-on-children`)
    // - DesignerExpansion::generateUpdatedAttributes
    if (hasCmsChildrenBinding) {
      map.set(CMS_BINDING_ON_CHILDREN_ATTR, true);
    } else if (hasBinding) {
      map.set(BINDING_ON_CHILDREN_ATTR, true);
    }

    /**
     * Attach additional attributes to the node for bound items, containing the CMS collection & item ids.
     */
    if (
      canNavigateFromPageToCMS &&
      (isDynBoundElement(element) ||
        hasDynamoUniversalBindings(element) ||
        isDynamoItem(element) ||
        isCMSBoundComponentRoot(
          expandedNativeIdPath,
          components,
          environmentContext.get(focusedSymbolKey) ?? [],
          isComponentRootElement
        ))
    ) {
      const collectionId = pluginsContext.getIn(['Dynamo', 'collectionId']);
      const itemId = pluginsContext.getIn(['Dynamo', 'itemId']);
      const collection = pluginsContext
        .getIn(['Dynamo', 'collections'])
        ?.get(collectionId);

      if (collectionId && itemId) {
        map
          .set(CMS_COLLECTION_ID_ATTR, collectionId)
          .set(CMS_ITEM_ID_ATTR, itemId)
          .set(
            CMS_COLLECTION_SOURCE_ATTR,
            isCommerceCollection(collection) ? 'commerce' : 'cms'
          );
      }
    }
  });
};

type GetGridEditingStylesParams = {
  element: ExpressionElement;
  isOpenCanvas: boolean;
};

const getGridEditingStyles = ({
  element,
  isOpenCanvas,
}: GetGridEditingStylesParams) => {
  // Filter is applied when editing grid as follows:
  // - When on the Open Canvas, applies to the frame layout element
  // - When on the scroll-based canvas, applies to the body element
  const shouldApplyFilterOnFrameLayout =
    isOpenCanvas && isFrameLayoutElement(element);
  const shouldApplyFilterOnBody = !isOpenCanvas && isBody(element);

  const shouldAddFilter =
    shouldApplyFilterOnFrameLayout || shouldApplyFilterOnBody;
  return shouldAddFilter
    ? {
        filter: GRID_EDITING_FILTER,
      }
    : emptyObject;
};

function usePreviousProps<T>(props: T) {
  const ref = React.useRef<T | null>(null);
  const previousProps = ref.current;
  ref.current = props;
  return previousProps;
}

const expandAtom = (
  render: AtomNormalizedRender,
  atomConfig: NormalizedAtomConfig
) => {
  return function DesignerAtom(props: ExpansionProps) {
    const runtimeEnvironment = useRuntimeEnvironment();
    const previousProps = usePreviousProps(props);
    const prevElement = React.useRef<React.ReactElement | null>(null);
    const previewMode = usePreviewMode();
    const cssPropertyMap = useCssPropertyMap();
    const designerShowEmptyEnabled = useShowEmpty();
    const analyzeMode = useAnalyzeMode();
    const isOpenCanvas = useOpenCanvas();
    const itemNamesById = useDesignerExpansionItems();

    const {
      classes,
      components,
      data,
      environmentContext,
      nativeIdPath,
      element,
    } = props;

    // NOTE: nativeIdPath doesn't include the current node and can therefore be empty.
    // "fullNativeIdPath" has it appended here
    const fullNativeIdPath = nativeIdPath.concat(
      computeNativeId(element.id, environmentContext)
    );
    const showEmptyForBuildMode = useShowEmptyForBuildModeByKey(
      fullNativeIdPath.join('+')
    );

    const selectedNativeIdPath = environmentContext.get(
      selectedNativeIdPathKey
    );

    const isElementInSelectedPath =
      elementContainsNestedSelection(selectedNativeIdPath)(element);

    const pseudoMode = isElementInSelectedPath
      ? environmentContext.get('pseudoMode')
      : null;

    const affectedNodeIds = environmentContext.get('affectedNodeIds');
    const variantState = environmentContext.get('variantState');

    const mediaQuery = useMediaQueryWhen((mq) => {
      if (!mq) {
        return false;
      }

      return (
        shouldAddEmptyClass(
          element,
          cssPropertyMap,
          mq,
          pseudoMode,
          variantState,
          data
        ) ||
        shouldAddGridClass(
          element,
          cssPropertyMap,
          mq,
          pseudoMode,
          variantState
        )
      );
    });

    /**
     * React skips re-rendering if the same elements are returned by reference.
     *
     * The selected node optimization relies on knowing what the previous props
     * were, not what the previously renderer props were. That means React.memo
     * with a custom comparison doesn't work since it compares with the previously
     * rendered props.
     *
     * Here we use a ref to store the previously attempted props.
     */
    if (previousProps && evalElementMemo(previousProps, props)) {
      return prevElement.current;
    }

    const editTargetNativeIdPath = environmentContext.get(
      'editTargetNativeIdPath'
    );
    const editTargetNativeId =
      isNativeIdPath(editTargetNativeIdPath) &&
      editTargetNativeIdPath[editTargetNativeIdPath.length - 1];

    const nativeId = fullNativeIdPath[fullNativeIdPath.length - 1];
    const nativeGuid = nativeId && getGuidNullable(nativeId);

    const editTargetRootNodeId =
      isNativeIdPath(editTargetNativeIdPath) &&
      editTargetNativeIdPath[editTargetNativeIdPath.length - 2];
    const editTargetNativeGuid =
      editTargetNativeId && getGuidNullable(editTargetNativeId);

    const focusedSymbolNativeIdPath = environmentContext.get(focusedSymbolKey);
    const isEditingSelectedNodeInInstance = _isEditingSelectedNodeInInstance(
      selectedNativeIdPath,
      focusedSymbolNativeIdPath
    );
    const isEditingThisInstance = isEditingSelectedNodeInInstance
      ? nativeId === editTargetNativeId &&
        isEqual(fullNativeIdPath, selectedNativeIdPath)
      : nativeId === editTargetNativeId;
    const isEditingAnotherInstanceInDynlist =
      !isEditingThisInstance && nativeGuid === editTargetNativeGuid;

    const isEditing =
      isEditingThisInstance ||
      isEditingAnotherInstanceInDynlist ||
      element.id === editTargetRootNodeId;
    const uniqueNativeId = __DEPRECATED__getNativeId(
      props,
      previewMode,
      runtimeEnvironment
    );

    const editSiteMode = environmentContext.get('editSiteMode');
    const showEmptyTypographicInEditMode =
      editSiteMode && isTextHolder(element);

    const canShowEmpty =
      designerShowEmptyEnabled ||
      showEmptyForBuildMode ||
      showEmptyTypographicInEditMode;

    const showEmpty =
      showEmptyWithNode(atomConfig.showEmpty, element) &&
      canShowEmpty &&
      !previewMode &&
      !isEditing;

    const {showEmptyAsBlock} = atomConfig;

    const designerClasses: ClassesList = classes.withMutations(
      (mut_classes) => {
        flow(
          createEmptyClassAdder(cssPropertyMap)(element)(
            showEmpty,
            showEmptyAsBlock,
            mediaQuery,
            pseudoMode,
            variantState,
            data
          ),
          createDropTargetOrSlotClassAdder(environmentContext)(components)(
            element
          ),
          createGridClassAdder(cssPropertyMap)(element)(
            mediaQuery,
            pseudoMode,
            variantState
          ),
          createDropAllowedClassAdder(environmentContext.get('dropTargets'))(
            element
          ),
          createPseudoClassAdder(pseudoMode),
          affectedNodeIds === null ||
            affectedNodeIds.length === 0 ||
            previewMode
            ? identity
            : createAffectedNodeClassAdder(affectedNodeIds)(element)
        )(mut_classes);
      }
    );

    const editingGrid = environmentContext.get('editingGrid');
    const gridEditingStyles = editingGrid
      ? getGridEditingStyles({element, isOpenCanvas})
      : emptyObject;

    const shouldShowWebflowComponentRootAttr =
      environmentContext.get('isNestedInTextHolder') &&
      isUserComponentRootElement(
        props.isComponentRootElement,
        props.seenComponents
      );

    const updatedAttributes = generateUpdatedAttributes(
      uniqueNativeId,
      props,
      fullNativeIdPath,
      previewMode,
      shouldShowWebflowComponentRootAttr
    );

    let updatedProps: ExpansionProps = props;

    if (
      updatedAttributes !== props.attributes ||
      designerClasses !== classes ||
      Object.keys(gridEditingStyles).length > 0
    ) {
      updatedProps = {
        plugins: props.plugins,
        pluginsContext: props.pluginsContext,
        element: props.element,
        data: props.data,
        components: props.components,
        env: props.env,
        isComponentRootElement: props.isComponentRootElement,
        seenComponents: props.seenComponents,
        environmentContext: props.environmentContext,
        nativeIdPath: props.nativeIdPath,
        sharedContext: props.sharedContext,
        componentContext: props.componentContext,
        trackingContext: props.trackingContext,
        fullEnv: props.fullEnv,
        evalExpr: props.evalExpr,
        legacyDynamoBindingPaths: props.legacyDynamoBindingPaths,
        attributes: updatedAttributes,
        classes: designerClasses,
        style: union<string>(gridEditingStyles)(props.style || emptyObject),
      };

      mut_copyBindingProps(props, updatedProps);
    }

    // Transform props with element tracking context
    const propsWithTracking = transformWithElementTrackingContextForDesigner(
      updatedProps,
      atomConfig,
      analyzeMode
    );

    const isTextNode = isTextHolder(element) || isTypographicCandidate(element);
    const isNestedInTextHolder = environmentContext.get('isNestedInTextHolder');
    // We only ever want ContentEditor on the furthest TextHolder. ContentEditor.jsx will set this
    // for all of its descendants
    const isFurthestTextHolder = isTextNode && !isNestedInTextHolder;

    // Allow editing text nodes inside rich text elements
    const allowNestedTextNodesInRichText = isTextNode && isNestedInTextHolder;

    if (isFurthestTextHolder || allowNestedTextNodesInRichText) {
      // Note: React will re-render the entire subtree if we re-parent textHolder nodes
      // in ContentEditor when starting/stopping editing. To get around that we
      // always wrap textHolder nodes in ContentEditor and toggle the isEditing prop to activate it
      // on a node.

      // Also note when it comes editing static symbols and dynlists we want
      // the other instances to be connected to the ContentEditorStore's
      // subtree (the thing that's being actively edited) so changes are
      // mirrored in real-time, but we don't want those other instances to be
      // contentEditable or listen for events or anything.

      prevElement.current = (
        <ContentEditor
          isEditing={isEditing}
          isEditingThisInstance={isEditingThisInstance}
          isEditingSelectedNodeInInstance={isEditingSelectedNodeInInstance}
          render={render}
          renderProps={propsWithTracking}
          latencyProfilerEventName="DesignerTextInputLatency"
          editTargetFieldIdPath={null}
        />
      );
    } else {
      prevElement.current = render(
        transformDynamoPropsForDesigner(propsWithTracking, itemNamesById)
      );
    }

    return prevElement.current;
  };
};

export default expandAtom;
